// Import dependencies
import initScrollReveal from "./scripts/scrollReveal";
import { targetElements, defaultProps } from "./data/scrollRevealConfig";
import VanillaTilt from "vanilla-tilt";

// Import Firebase config
import { db } from "./blog/firebase-config.js";

// Helper: Create element with attributes and classes
function createElement(tag, { attributes = {}, classes = [], text = "" } = {}) {
  const el = document.createElement(tag);
  Object.entries(attributes).forEach(([key, value]) =>
    el.setAttribute(key, value)
  );
  if (classes.length) el.classList.add(...classes);
  if (text) el.textContent = text;
  return el;
}

// Create post card element
function createPostCard(post, slug) {
  const { title, publishedAt, heroImageUrl } = post;

  const card = createElement("div", {
    classes: ["card", "rounded", "h-100", "js-tilt"],
    attributes: {
      "data-tilt": "",
      "data-tilt-max": "4",
      "data-tilt-glare": "true",
      "data-tilt-max-glare": "0.5",
    },
  });

  // Image setup with fallback
  const img = createElement("img", {
    attributes: {
      src: heroImageUrl || "./assets/images/fingers-sm.jpg", // Default/fallback image
      alt: `Cover image for ${title}`,
      style: "height: 200px; object-fit: cover;"
    },
  });

  card.appendChild(img);

  // Description setup
  const desc = createElement("div", { classes: ["desc"] });
  desc.appendChild(createElement("h3", { text: title || "Untitled Post" }));

  let displayDate = "";
  if (publishedAt) {
    const dateObj = publishedAt.toDate ? publishedAt.toDate() : new Date(publishedAt);
    displayDate = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  if (displayDate) {
    desc.appendChild(
      createElement("p", {
        classes: ["post-date"],
        text: `Published: ${displayDate}`,
      })
    );
  }

  card.appendChild(desc);

  // Link wrapper
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const postLink = slug 
    ? (isLocalhost ? `./blog/post.html?slug=${slug}` : `./blog/${slug}`) 
    : "#";

  const link = createElement("a", {
    classes: ["col", "d-flex", "blog-wrapper__card", "load-hidden"],
    attributes: {
      href: postLink,
    },
  });
  link.appendChild(card);

  return link;
}

// Create "Discover more" card
function createReadMoreCard() {
  // Create main card wrapper
  const card = createElement("div", {
    classes: ["card", "read-more-card", "rounded", "h-100", "w-100", "js-tilt"],
    attributes: {
      "data-tilt": "",
      "data-tilt-max": "4",
      "data-tilt-glare": "true",
      "data-tilt-max-glare": "0.5",
    },
  });

  // Add arrow container
  const arrowContainer = createElement("div", { classes: ["arrow-container"] });
  arrowContainer.innerHTML = `
    <svg class="arrow-right" viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  `;
  card.appendChild(arrowContainer);

  // Add description div
  const desc = createElement("div", { classes: ["desc"] });
  desc.appendChild(createElement("h3", { text: "Discover more blog posts" }));
  card.appendChild(desc);

  // Create the anchor wrapper
  const link = createElement("a", {
    classes: ["col", "d-flex", "blog-wrapper__card", "load-hidden"],
    attributes: {
      href: "./blog/index.html",
    },
  });
  link.appendChild(card);

  return link;
}

// Initialize tilt effect
function initTiltOnElements() {
  if (window.VanillaTilt) {
    VanillaTilt.init(document.querySelectorAll(".js-tilt"));
  }
}

// Fetch and render posts from Firestore
async function fetchAndRenderPosts() {
  const blogPosts = document.getElementById("blogPosts");
  if (!blogPosts) {
    console.error("Blog posts container not found!");
    return;
  }

  blogPosts.innerHTML = "<p>Loading blog posts...</p>";

  try {
    if (!db) {
      throw new Error("Firestore database is not configured. Ensure Firebase is initialized.");
    }

    const querySnapshot = await db.collection("posts")
      .where("status", "==", "published")
      .orderBy("publishedAt", "desc")
      .limit(3)
      .get();

    const fragment = document.createDocumentFragment();

    querySnapshot.forEach((docSnap) => {
      try {
        const postElement = createPostCard(docSnap.data(), docSnap.id);
        fragment.appendChild(postElement);
      } catch (error) {
        console.error("Error creating post card:", error);
      }
    });

    const readMoreCard = createReadMoreCard();
    fragment.appendChild(readMoreCard);

    blogPosts.innerHTML = "";
    blogPosts.appendChild(fragment);

    initTiltOnElements();
    initScrollReveal(targetElements, defaultProps);
  } catch (error) {
    console.error("Error fetching blog posts from Firestore:", error);
    blogPosts.innerHTML = `
      <div class="error-message p-3 border rounded text-center">
        <p>Failed to load blog posts. Please try again later.</p>
        <p class="error-details small text-muted">${error.message}</p>
      </div>
    `;
  }
}

// Navigation toggle
function toggleMenu() {
  const nav = document.querySelector(".Navbar__Items");
  const icon = document.querySelector(".Navbar__Link-toggle i");
  nav.classList.toggle("Navbar__ToggleShow");
  icon?.classList.toggle(
    "toggle",
    nav.classList.contains("Navbar__ToggleShow")
  );
}

// Initialize everything
document.addEventListener("DOMContentLoaded", () => {
  fetchAndRenderPosts();
  initScrollReveal(targetElements, defaultProps);
  document
    .querySelector(".Navbar__Link-toggle")
    .addEventListener("click", toggleMenu);
});
